import type {
  ApiResponse,
  UpdateUserProfileInput,
  User,
  UserListQueryParams,
  UserProfile,
  UserRole,
  UsersListResponse,
} from "types";
import { graphqlClient } from "../graphql/client";

// ============ GraphQL 查询字符串 ============
// 获取当前用户
const CURRENT_USER_QUERY = `
  query UserProfile {
    userProfile {
        id
        email
        username
        isOnline
        roles {
            id
            name
            permissions {
                id
                name
                action
            }
        }
        profile {
            name
            picture
            qrCode
            gender
            region
            mobile
            signature
        }
    }
  }
`;
const USERS_LIST_QUERY = `
  query UsersList($page: Int!, $limit: Int!, $filter: UserFilter) {
    usersList(query: { page: $page, limit: $limit, filter: $filter }) {
        meta {
            itemsPerPage
            totalItems
            currentPage
            totalPages
        }
        data {
            id
            email
            username
            status
            isOnline
            createdBy
            createdDate
            updatedBy
            updatedDate
            roles {
                id
                name
            }
            profile {
                id
                name
                picture
                qrCode
                gender
                region
                mobile
                signature
                createdDate
                updatedDate
            }
        }
    }
}

`;

const USER_DETAIL_QUERY = `
  query UserDetail($id: Int!) {
    userDetail(id: $id) {
      id
      email
      username
      status
      profile {
        id
        name
        picture
        qrCode
        gender
        region
        mobile
      }
      roles {
        id
        name
      }
    }
  }
`;

const CREATE_USER_MUTATION = `
  mutation CreateUser($input: CreateUserDto!) {
    createUser(createUserDto: $input) {
      id
      email
      username
      status
      profile {
        id
        name
        picture
        qrCode
        gender
        region
        mobile
      }
      roles {
        id
        name
      }
    }
  }
`;

const UPDATE_USER_MUTATION = `
  mutation UpdateUser($id:Int!,$input: UpdateUserDto!) {
    updateUser(id:$id,updateUserDto: $input) {
      id
      email
      username
      status
      profile {
        id
        name
        picture
        qrCode
        gender
        region
        mobile
      }
      roles {
        id
        name
      }
    }
  }
`;

const UPDATE_PROFILE_MUTATION = `
  mutation UpdateUserProfile($input:UpdateUserProfileDto!) {
    updateUserProfile(updateUserDto: $input) {
        id
        name
        picture
        gender
        mobile
    }
  }
`;

const DELETE_USER_MUTATION = `
  mutation RemoveUser($id: Int!) {
    removeUser(id: $id){
      result
    }
  }
`;

const GET_ROLES_QUERY = `
  query RoleList {
    roleList {
        id
        name
    }
}
`;

// ============ API 函数 ============
/**
 * 获取当前登录用户信息
 * @returns 当前用户信息
 */
export async function getCurrentUser(): Promise<ApiResponse<User>> {
  const response = await graphqlClient.request<{
    userProfile: User;
  }>(CURRENT_USER_QUERY);

  return {
    ...response,
    data: response.data?.userProfile,
  };
}
/**
 * 获取用户列表
 */
export async function getUsersList(
  params: UserListQueryParams = {}
): Promise<ApiResponse<UsersListResponse>> {
  const variables = {
    page: params.page || 1,
    limit: params.limit || 10,
  };

  const response = await graphqlClient.request<{
    usersList: UsersListResponse;
  }>(USERS_LIST_QUERY, variables);

  return {
    ...response,
    data: response.data?.usersList,
  };
}

/**
 * 获取用户详情
 */
export async function getUserDetail(id: string): Promise<ApiResponse<User>> {
  const response = await graphqlClient.request<{
    userDetail: User;
  }>(USER_DETAIL_QUERY, { id });

  return {
    ...response,
    data: response.data?.userDetail,
  };
}

/**
 * 创建用户
 */
export async function createUser(input: any): Promise<ApiResponse<User>> {
  const response = await graphqlClient.request<{
    createUser: User;
  }>(CREATE_USER_MUTATION, { input });

  return {
    ...response,
    data: response.data?.createUser,
  };
}

/**
 * 更新用户信息
 */
export async function updateUser(
  id: number,
  input: any
): Promise<ApiResponse<User>> {
  const response = await graphqlClient.request<{
    updateUser: User;
  }>(UPDATE_USER_MUTATION, { id, input });

  return {
    ...response,
    data: response.data?.updateUser,
  };
}

/**
 * 
 * @param id 更新个人资料
 * @returns 
 */
export async function updateProfile(input:UpdateUserProfileInput): Promise<ApiResponse<UserProfile>>{
  const response = await graphqlClient.request<{
    updateUserProfile: UserProfile;
  }>(UPDATE_PROFILE_MUTATION, { input });

  return {
    ...response,
    data: response.data?.updateUserProfile,
  };
}
/**
 * 删除用户
 */
export async function deleteUser(id: string): Promise<ApiResponse<boolean>> {
  const response = await graphqlClient.request<{
    removeUser: { result: string };
  }>(DELETE_USER_MUTATION, { id });

  return {
    ...response,
    data: response.data?.removeUser.result === "success",
  };
}

/**
 * 获取所有角色
 */
export async function getRoles(): Promise<ApiResponse<UserRole[]>> {
  const response = await graphqlClient.request<{
    roleList: UserRole[];
  }>(GET_ROLES_QUERY);

  return {
    ...response,
    data: response.data?.roleList,
  };
}
